import React, { useState } from 'react';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { Image, FileText, X, ZoomIn } from 'lucide-react';
import { useTranslation } from '../contexts/LanguageContext';

export default function ImageGallery({ images = [], blueprints = [], title }) {
  const { t } = useTranslation();
  const galleryTitle = title || t('common.gallery');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  // Normalize blueprints - support both string URLs and objects {url, original_name}
  const normalizeDoc = (b) => typeof b === 'string' ? { url: b, original_name: null } : b;
  const normalizedDocs = blueprints.map(normalizeDoc);

  const allImages = [...images, ...normalizedDocs.filter(b => isImageUrl(b.url)).map(b => b.url)].filter(Boolean);

  if (allImages.length === 0 && normalizedDocs.length === 0) return null;

  const pdfBlueprints = normalizedDocs.filter(b => !isImageUrl(b.url));

  return (
    <div data-testid="image-gallery">
      {/* Images Grid */}
      {allImages.length > 0 && (
        <div>
          <h4 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
            <Image className="w-4 h-4 text-[#1B5E37]" />
            {galleryTitle}
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {allImages.map((img, index) => (
              <button
                key={index}
                onClick={() => { setPhotoIndex(index); setLightboxOpen(true); }}
                className="relative group aspect-square rounded-lg overflow-hidden border border-border hover:border-[#1B5E37] transition-colors"
                data-testid={`gallery-image-${index}`}
              >
                <img 
                  src={img} 
                  alt={`${galleryTitle} ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"

                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* PDF Blueprints */}
      {pdfBlueprints.length > 0 && (
        <div className="mt-4">
          <h4 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#1B5E37]" />
            Документи
          </h4>
          <div className="flex flex-wrap gap-2">
            {pdfBlueprints.map((doc, index) => (
              <a
                key={index}
                href={doc.url}
                download={doc.original_name || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-secondary border border-border rounded-lg hover:border-[#1B5E37] transition-colors text-sm"
                data-testid={`gallery-doc-${index}`}
              >
                <FileText className="w-4 h-4 text-[#1B5E37]" />
                {doc.original_name || `${t('property.document')} ${index + 1}`}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && allImages.length > 0 && (
        <Lightbox
          mainSrc={allImages[photoIndex]}
          nextSrc={allImages.length > 1 ? allImages[(photoIndex + 1) % allImages.length] : undefined}
          prevSrc={allImages.length > 1 ? allImages[(photoIndex + allImages.length - 1) % allImages.length] : undefined}
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() => setPhotoIndex((photoIndex + allImages.length - 1) % allImages.length)}
          onMoveNextRequest={() => setPhotoIndex((photoIndex + 1) % allImages.length)}
          imageTitle={`${galleryTitle} - ${photoIndex + 1} / ${allImages.length}`}
        />
      )}
    </div>
  );
}

function isImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
  const lower = url.toLowerCase();
  // Only check extensions and Cloudinary image URLs - never treat /api/files/ or PDFs as images
  if (lower.includes('.pdf') || lower.includes('/raw/upload/')) return false;
  return imageExts.some(ext => lower.includes(ext)) || lower.includes('/image/upload/');
}
